import React, { useCallback, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import Text from '../../../library/text/text';

import {
  useMyAccount,
  useTreadwellPreferences
} from '../../../shared/hooks/hooks';

import {
  getIsAppBasedDriver,
  getMilesDrivenAnnuallyDefaultValue
} from '../../../shared/utils/treadwell/treadwell';

import styles from './annual-miles-input.scss';

const MAX_ANNUAL_MILES = 99999;
const MIN_ANNUAL_MILES = 0;

interface AnnualMilesInputProps {
  onChange?: (annualMiles) => void;
}

function AnnualMilesInput(props: AnnualMilesInputProps) {
  const { onChange } = props;
  const { isFleetAccount } = useMyAccount();
  const { saveTreadwellPreferences, treadwellPreferences } =
    useTreadwellPreferences();
  const isAppBasedDriver = getIsAppBasedDriver();
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;
  const defaultMilesDrivenAnnually = getMilesDrivenAnnuallyDefaultValue({
    isAppBasedDriver,
    isFleetTreadwell
  });
  const [milesDrivenAnnually, setMilesDrivenAnnually] = useState(
    Number(
      treadwellPreferences?.selectedDrivingPreferences?.milesDrivenAnnually ||
        defaultMilesDrivenAnnually
    )
  );

  const debouncedSaveTreadwellPreferences = useMemo(
    () =>
      debounce((annualMiles, treadwellPreferences) => {
        treadwellPreferences &&
          saveTreadwellPreferences({
            ...treadwellPreferences,
            selectedDrivingPreferences: {
              ...treadwellPreferences.selectedDrivingPreferences,
              milesDrivenAnnually: annualMiles?.toString()
            }
          });
      }, 1000),
    [saveTreadwellPreferences]
  );

  const onChangeAnnualMiles = useCallback(
    e => {
      let annualMiles = parseInt(e.target.value);

      if (!isNaN(annualMiles)) {
        if (annualMiles < MIN_ANNUAL_MILES) {
          annualMiles = MIN_ANNUAL_MILES;
        } else if (annualMiles > MAX_ANNUAL_MILES) {
          annualMiles = MAX_ANNUAL_MILES;
        }
      } else {
        annualMiles = MIN_ANNUAL_MILES;
      }

      setMilesDrivenAnnually(annualMiles);
      onChange?.(annualMiles);
      if (isFleetTreadwell || isAppBasedDriver) {
        debouncedSaveTreadwellPreferences(annualMiles, treadwellPreferences);
      }
    },
    [
      debouncedSaveTreadwellPreferences,
      isAppBasedDriver,
      isFleetTreadwell,
      onChange,
      treadwellPreferences
    ]
  );

  return (
    <Text
      className={styles['annual-miles-input']}
      inputMode="numeric"
      isRequired
      label="Estimated annual miles"
      name="annual-miles"
      onChange={onChangeAnnualMiles}
      type="number"
      value={milesDrivenAnnually?.toString()}
    />
  );
}

export default AnnualMilesInput;
